import * as ApiHelper from "../../api/ApiHelper";
import { formatNullable } from "../../api/ApiHelper";
import type { LineItem, SubLineItem } from "../models/CartData";
import { isProductPMA } from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { isKITProduct } from "../../models/ProductData";
import { toMapFn } from "../../utils/reducers";
import { naiveTrackCartContents } from "~/feature/Analytics/ga/abandonment";
import { resolveBulkAddUrl } from "~/utils/url/urlUtils";
import { registerCallProcessor, scheduleCall } from "~/Cart/api/CartService";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { getCurrentBrowserTimezone } from "~/utils/dateUtils";

export interface AddToCartItem {
  productCode: string;
  packageSize?: number;
  packageCode: string;
  quantity: number;
  netPrice: number;
  priceUnit: number;
  comment?: string;
  selectedDeliveryDate: Date | null;
  selectedDeliveryDateIsManual: boolean;
  // estimatedDeliveryDate?: Date,
  primaryWarehouseCode: string;
  contractNumber?: string;
  contractLine?: string;
  fifteenDigit: boolean;
  priceReference: string;
  isPMA: boolean;

  // Quote specific fields
  quoteLine?: string;
  quoteNumber?: string;
  quoteScheduleLineNum?: string;
  quoteMinQuantity?: number;
  isBundled?: boolean;
  quoteExpiryDate?: Date | null;
  logisticalComment?: string;
  estimatedShippingDate?: Date | null;

  restrictedPackage: boolean;
  optionalParts?: AddToCartPartItem[];
  surcharge: number;
  tariffSurchargeApplicable: boolean;
  customerOrderNumber?: string;
  originCountry: string;

  // Sales campaign stuff
  specialPrice?: boolean;
  saleNetPrice?: number;
  salesCampaignCode?: string;

  // For contracts
  contractOpenQuantity?: number;

  // Recommendation
  recommendationType?: string;
  recommendationPlace?: string;
}

export interface AddToCartPartItem {
  productCode: string;
  quantity: number;
  packageCode: string;
  packageSize: number;
  netPrice: number;
  priceUnit: number;
  primaryWarehouseCode: string;
  partTypeCode: string;
  manualUpdated: boolean;
  surcharge: number;
  originCountry: string;
}

export interface AddToCartApiItem {
  productCode: string;
  packageSize?: number;
  packageCode: string;
  quantity: number;

  /**
   * TODO: Kick this out of ADD TO CART API, it's revelant only for quote stuff.
   */
  confirmedQuantity?: number;
  /**
   * Needs to be per piece or BE freaks out
   * TODO: Update BE to accept per {unitPrice} pieces
   */
  netPrice: number;
  priceUnit: number;
  comment?: string;
  selectedDeliveryDate?: string;
  selectedDeliveryDateIsManual: boolean;
  // estimatedDeliveryDate?: string;
  primaryWarehouseCode?: string;
  pnaCall?: boolean;
  contractNumber?: string;
  contractLine?: string;
  fifteenDigit: boolean;
  priceReference?: string;

  // Quote specific fields
  quoteLine?: string;
  quoteNumber?: string;
  quoteScheduleLineNum?: string;
  quoteMinQuantity?: number;
  isBundled?: boolean;

  quoteExpiryDate?: string;
  logisticalComment?: string;
  estimatedShippingDate?: string;

  isRestrictedPackage: boolean;

  optionalParts?: AddToCartPartItem[];
  surcharge: number;

  customerOrderNumber?: string;
  originCountry: string;

  // Sales campaign stuff
  specialPrice?: boolean;
  saleNetPrice?: number;
  salesCampaignCode?: string;
}

function transform(item: AddToCartItem): AddToCartApiItem {
  const rdd = item.selectedDeliveryDate;
  // const edd = item.estimatedDeliveryDate;
  return {
    ...item,
    selectedDeliveryDate: formatNullable(rdd),
    quoteExpiryDate: formatNullable(item.quoteExpiryDate),
    estimatedShippingDate: formatNullable(item.estimatedShippingDate),
    isRestrictedPackage: item.restrictedPackage,
    // estimatedDeliveryDate: edd ? format(edd) : undefined,
  };
}

export function fromLineItem(data: LineItem, subLineItems: SubLineItem[], product: ProductDescriptor): AddToCartItem {
  const subProductTypes = isKITProduct(product.base)
    ? toMapFn(
        product.base.optionalParts,
        (p) => p.product.code,
        (p) => p.type,
      )
    : {};

  let netPrice;
  if (data.quoteLine || data.contractLine) {
    netPrice = data.netPrice?.value ?? 0;
  } else {
    netPrice = data.actualNetPrice?.value ?? 0;
  }

  return {
    productCode: product.base.code,
    packageSize: data.packSize,
    packageCode: data.packCode,
    quantity: data.quantity,
    netPrice: netPrice,
    priceUnit: data.netPrice?.unit ?? 1,
    comment: data.comment,
    selectedDeliveryDate: data.deliveryDate,
    selectedDeliveryDateIsManual: data.manuallySetRDD,
    // estimatedDeliveryDate: (data.edd && data.edd.type === 'estimate') ? data.edd.date : undefined,
    primaryWarehouseCode: data.primaryWarehouseCode,
    contractNumber: data.contractNumber,
    contractLine: data.contractLine,
    fifteenDigit: product.isMultiPrice || false,
    priceReference: data.priceReference,
    isPMA: isProductPMA(product),

    // Quote specific fields
    quoteLine: data.quoteLine.split(":")[0],
    quoteScheduleLineNum: data.quoteLine.split(":")[1],
    quoteNumber: data.quoteNumber,
    quoteMinQuantity: data.minimumQty,
    isBundled: data.isBundled,

    quoteExpiryDate: data.quoteExpiryDate,
    logisticalComment: data.logisticalComment,
    estimatedShippingDate: data.estimatedShippingDate,

    restrictedPackage: data.restrictedPackage,

    optionalParts:
      subLineItems.length === 0
        ? undefined
        : subLineItems.map((item) => ({
            productCode: item.productCode,
            quantity: item.quantity,
            netPrice: item.netPrice?.value ?? 0,
            priceUnit: item.netPrice?.unit ?? 1,
            primaryWarehouseCode: item.primaryWarehouseCode,
            packageCode: item.packCode,
            packageSize: item.packSize ?? 1,
            manualUpdated: true,
            partTypeCode: subProductTypes[item.productCode] ?? "",
            surcharge: item.netPrice?.surcharge ?? 0,
            originCountry: item.countryOfOrigin,
          })),
    surcharge: data.netPrice?.surcharge ?? 0,
    tariffSurchargeApplicable: !!data.netPrice?.tariffSurcharge,
    customerOrderNumber: data.customerOrderNumber,
    originCountry: data.countryOfOrigin,

    specialPrice: data.saleCampaignEnabled,
    salesCampaignCode: data.saleCampaignCode,
    saleNetPrice: data.saleCampaignEnabled ? data.actualSalePrice?.value ?? 0 : undefined,
  };
}

export function sendAddToCart(item: AddToCartItem | AddToCartItem[], skipTracking = false) {
  return sendBulkAddToCart(Array.isArray(item) ? item : [item], skipTracking);
}

export function sendBulkAddToCart(items: AddToCartItem[], skipTracking = false) {
  return scheduleCall("add", { items, skipTracking });
}

const apiUrl = resolveBulkAddUrl();
registerCallProcessor({
  type: "add",
  canBeExecuted: (ongoingCalls) => {
    const isDeleteOngoing = ongoingCalls.some((call) => call.type === "delete" || call.type === "deleteCart");
    return !isDeleteOngoing;
  },
  execute: async ({ items, skipTracking }: { items: AddToCartItem[]; skipTracking: boolean }) => {
    const body = {
      items: items.map(transform),
      userTimeZone: getCurrentBrowserTimezone(),
    };

    const packageToCartForm = JSON.stringify(body);

    return fetch(apiUrl, {
      body: packageToCartForm,
      cache: "no-cache",
      credentials: "same-origin",
      method: "POST",
      mode: "cors",
      headers: {
        dataType: "json",
        "content-type": "application/json",
        CSRFToken: ApiHelper.getCsrfValue(),
      },
      redirect: "follow",
    })
      .then((response) => {
        if (response.redirected) {
          window.location.reload();
        }
        return response;
      })
      .then((result) => {
        window.SCHA.updateMinicart?.();
        return result;
      })
      .then((result) => {
        if (!skipTracking) {
          naiveTrackCartContents();
        }
        return result;
      });
  },
});
